/**
 * Dealer Application Form Handler
 * Handles form submission, validation, and success/error states
 */

class DealerApplicationForm {
  constructor() {
    this.form = document.getElementById('dealer-application-form');
    if (!this.form) return;

    this.autocomplete = null;
    this.init();
  }

  init() {
    // Set today's date as default for signature date
    const dateInput = this.form.querySelector('#signature_date');
    if (dateInput && !dateInput.value) {
      const today = new Date().toISOString().split('T')[0];
      dateInput.value = today;
    }

    // Handle form submission
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));

    // Enable/disable "Other" input fields based on radio selection
    this.setupConditionalFields();

    // Auto-fill contact email if business email is entered
    this.setupEmailAutofill();

    // Form validation
    this.setupValidation();

    // Initialize Google Places Autocomplete
    this.initGooglePlacesAutocomplete();
  }

  /**
   * Initialize Geoapify Autocomplete
   */
  initGooglePlacesAutocomplete() {
    this.setupGeoapifyAutocomplete();
  }

  /**
   * Setup Geoapify autocomplete functionality
   */
  setupGeoapifyAutocomplete() {
    const addressInput = document.getElementById('shipping_street_address');
    const countrySelect = document.getElementById('shipping_country');
    
    if (!addressInput || !countrySelect) return;

    // IMPORTANT: Replace with your Geoapify API key
    // Get free key at: https://www.geoapify.com/
    const GEOAPIFY_API_KEY = 'f5fb893965d3486196fdf22be883377c';
    
    let currentCountry = '';
    let debounceTimeout = null;
    let autocompleteContainer = null;

    // Create autocomplete results container
    autocompleteContainer = document.createElement('div');
    autocompleteContainer.className = 'geoapify-autocomplete-results';
    autocompleteContainer.style.cssText = `
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      z-index: 1000;
      background: white;
      border: 1px solid #ced4da;
      border-top: none;
      border-radius: 0 0 4px 4px;
      max-height: 300px;
      overflow-y: auto;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      margin-top: 0;
      display: none;
    `;
    addressInput.parentElement.style.position = 'relative';
    addressInput.parentElement.appendChild(autocompleteContainer);

    // Country select change handler
    countrySelect.addEventListener('change', () => {
      currentCountry = countrySelect.value;
      
      if (countrySelect.value) {
        addressInput.disabled = false;
        addressInput.placeholder = 'Start typing address for suggestions...';
        
        // Update country name field
        const countryNameField = document.getElementById('shipping_country_name');
        if (countryNameField) {
          countryNameField.value = countrySelect.options[countrySelect.selectedIndex].text;
        }
      } else {
        addressInput.disabled = true;
        addressInput.placeholder = 'Please select a country first';
        autocompleteContainer.style.display = 'none';
      }
    });

    // Initialize
    const selectedCountry = countrySelect.value;
    if (!selectedCountry) {
      addressInput.disabled = true;
      addressInput.placeholder = 'Please select a country first';
    } else {
      currentCountry = selectedCountry;
    }

    // Address input handler with debouncing
    addressInput.addEventListener('input', (e) => {
      const query = e.target.value.trim();
      
      clearTimeout(debounceTimeout);
      
      if (query.length < 3) {
        autocompleteContainer.style.display = 'none';
        return;
      }

      debounceTimeout = setTimeout(() => {
        this.searchGeoapifyAddress(query, currentCountry, GEOAPIFY_API_KEY, autocompleteContainer, addressInput);
      }, 300);
    });

    // Hide results when clicking outside
    document.addEventListener('click', (e) => {
      if (!addressInput.contains(e.target) && !autocompleteContainer.contains(e.target)) {
        autocompleteContainer.style.display = 'none';
      }
    });

    // Show results when focusing on input with existing text
    addressInput.addEventListener('focus', () => {
      if (addressInput.value.length >= 3 && autocompleteContainer.children.length > 0) {
        autocompleteContainer.style.display = 'block';
      }
    });
  }

  /**
   * Search for addresses using Geoapify API
   */
  async searchGeoapifyAddress(query, countryCode, apiKey, container, inputElement) {
    try {
      const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(query)}&filter=countrycode:${countryCode.toLowerCase()}&format=json&apiKey=${apiKey}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        this.displayGeoapifyResults(data.results, container, inputElement);
      } else {
        container.style.display = 'none';
      }
    } catch (error) {
      console.error('Geoapify API error:', error);
      container.style.display = 'none';
    }
  }

  /**
   * Display autocomplete results
   */
  displayGeoapifyResults(results, container, inputElement) {
    container.innerHTML = '';
    
    results.slice(0, 5).forEach(result => {
      const item = document.createElement('div');
      item.className = 'geoapify-result-item';
      item.style.cssText = `
        padding: 0.75rem 1rem;
        cursor: pointer;
        border-bottom: 1px solid #e9ecef;
        transition: background-color 0.2s;
      `;
      item.textContent = result.formatted;
      
      item.addEventListener('mouseenter', () => {
        item.style.backgroundColor = '#f8f9fa';
      });
      
      item.addEventListener('mouseleave', () => {
        item.style.backgroundColor = 'white';
      });
      
      item.addEventListener('click', () => {
        this.fillGeoapifyAddress(result, inputElement);
        container.style.display = 'none';
      });
      
      container.appendChild(item);
    });
    
    container.style.display = 'block';
  }

  /**
   * Fill in address fields from selected Geoapify result
   */
  fillGeoapifyAddress(place, inputElement) {
    console.log('Selected place:', place);
    
    // Fill in street address
    const street = place.street || place.address_line1 || '';
    const houseNumber = place.housenumber || '';
    const streetAddress = houseNumber ? `${houseNumber} ${street}` : street;
    
    document.getElementById('shipping_street_address').value = streetAddress || place.formatted;
    
    // Fill in city
    const city = place.city || place.county || '';
    document.getElementById('shipping_city').value = city;
    
    // Fill in state/province
    const state = place.state || place.state_code || '';
    document.getElementById('shipping_state').value = state;
    
    // Fill in postal code
    const postalCode = place.postcode || '';
    document.getElementById('shipping_postal_code').value = postalCode;
    
    console.log('Address filled:', {
      street: streetAddress,
      city: city,
      state: state,
      postal: postalCode
    });
  }

  /**
   * Handle form submission
   */
  handleSubmit(e) {
    // Don't prevent default - let form submit to iframe
    // e.preventDefault(); // REMOVED - we want the form to submit normally to the iframe

    // Validate form
    if (!this.form.checkValidity()) {
      e.preventDefault();
      this.form.reportValidity();
      return;
    }

    // Show loading state
    this.form.classList.add('is-submitting');
    const submitButton = this.form.querySelector('button[type="submit"]');
    const originalText = submitButton.textContent;
    submitButton.disabled = true;
    submitButton.textContent = 'Submitting...';

    // Add timestamp to form data
    const timestamp = new Date().toISOString();
    let timestampInput = this.form.querySelector('input[name="contact[submission_timestamp]"]');
    if (!timestampInput) {
      timestampInput = document.createElement('input');
      timestampInput.type = 'hidden';
      timestampInput.name = 'contact[submission_timestamp]';
      this.form.appendChild(timestampInput);
    }
    timestampInput.value = timestamp;

    // Format and add email body
    const emailBody = this.formatEmailBody(new FormData(this.form), timestamp);
    let bodyInput = this.form.querySelector('input[name="contact[body]"]');
    if (!bodyInput) {
      bodyInput = document.createElement('input');
      bodyInput.type = 'hidden';
      bodyInput.name = 'contact[body]';
      this.form.appendChild(bodyInput);
    }
    bodyInput.value = emailBody;

    // Form will submit to hidden iframe, preventing page reload
    // Wait a moment for the submission to complete, then show success
    setTimeout(() => {
      console.log('Form submitted successfully to iframe!');
      
      // Show success message
      this.showSuccess();
      
      // Scroll to success message
      setTimeout(() => {
        const successElement = document.querySelector('.dealer-application__success');
        if (successElement) {
          successElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);
      
      // Reset form after 5 seconds so user can see success message
      setTimeout(() => {
        this.form.reset();
      }, 5000);

      // Reset button state
      this.form.classList.remove('is-submitting');
      submitButton.disabled = false;
      submitButton.textContent = originalText;
    }, 1000); // Wait 1 second for form to submit
  }

  /**
   * Format email body with all application details
   */
  formatEmailBody(formData, timestamp) {
    const data = {};
    for (let [key, value] of formData.entries()) {
      if (key.startsWith('contact[') && key.endsWith(']')) {
        const fieldName = key.slice(8, -1);
        data[fieldName] = value;
      }
    }

    const formatDate = new Date(timestamp).toLocaleString('en-US', {
      dateStyle: 'full',
      timeStyle: 'short'
    });

    return `
═══════════════════════════════════════════════════════
    🚨 NEW WHOLESALE DEALER APPLICATION 🚨
═══════════════════════════════════════════════════════

Submitted: ${formatDate}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📋 BUSINESS INFORMATION
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Legal Business Name:     ${data.legal_business_name || 'N/A'}
Trade Name (DBA):        ${data.trade_name || 'N/A'}
Business Type:           ${data.business_type || 'N/A'}${data.business_type_other ? ' (' + data.business_type_other + ')' : ''}

Physical Address:        ${data.physical_address || 'N/A'}
Mailing Address:         ${data.mailing_address || 'Same as physical'}

Phone:                   ${data.phone_number || 'N/A'}
Email:                   ${data.email || 'N/A'}
Website:                 ${data.website || 'N/A'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
👤 PRIMARY CONTACT
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Name:                    ${data.primary_contact_name || 'N/A'}
Title/Position:          ${data.title_position || 'N/A'}
Direct Phone:            ${data.direct_phone || 'N/A'}
Email:                   ${data.contact_email || 'N/A'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📄 LEGAL & TAX INFORMATION
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Tax ID / EIN / VATIN:    ${data.tax_id || 'N/A'}
Resale Certificate:      ${data.resale_certificate || 'Not provided'}
Business License:        ${data.business_license || 'Not provided'}
State/Country:           ${data.state_incorporation || 'N/A'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
💳 PAYMENT & SHIPPING
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Payment Method:          ${data.payment_method || 'N/A'}${data.payment_method_other ? ' (' + data.payment_method_other + ')' : ''}

Shipping Address:
${data.shipping_business_name ? data.shipping_business_name + '\n' : ''}${data.shipping_street_address || 'N/A'}
${data.shipping_address_line2 ? data.shipping_address_line2 + '\n' : ''}${data.shipping_city || 'N/A'}, ${data.shipping_state || 'N/A'} ${data.shipping_postal_code || 'N/A'}
${data.shipping_country_name || data.shipping_country || 'N/A'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📝 ADDITIONAL INFORMATION
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

How They Heard About Us:    ${data.hear_about_us || 'N/A'}
Estimated Annual Volume:     ${data.estimated_annual_volume || 'N/A'}

Additional Comments:
${data.additional_comments || 'None'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
✍️ AGREEMENT & SIGNATURE
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Authorized Signature:    ${data.authorized_signature || 'N/A'}
Printed Name:            ${data.printed_name || 'N/A'}
Date:                    ${data.signature_date || 'N/A'}
Terms Accepted:          ${data.agree_to_terms === 'yes' ? '✓ YES' : '✗ NO'}

═══════════════════════════════════════════════════════

⚡ NEXT STEPS:
1. Review the application details above
2. Verify business credentials and tax information
3. Contact the applicant within 3-5 business days
4. If approved, set up dealer account and pricing

═══════════════════════════════════════════════════════
    `.trim();
  }

  /**
   * Show success message
   */
  showSuccess() {
    const messagesContainer = document.querySelector('.dealer-application__messages');
    const successMessage = messagesContainer.querySelector('.dealer-application__success');
    const errorMessage = messagesContainer.querySelector('.dealer-application__error');

    messagesContainer.style.display = 'block';
    successMessage.style.display = 'block';
    errorMessage.style.display = 'none';
  }

  /**
   * Show error message
   */
  showError(errorDetails) {
    const messagesContainer = document.querySelector('.dealer-application__messages');
    const successMessage = messagesContainer.querySelector('.dealer-application__success');
    const errorMessage = messagesContainer.querySelector('.dealer-application__error');
    const errorDetailsElement = messagesContainer.querySelector('.dealer-application__error-details');

    messagesContainer.style.display = 'block';
    successMessage.style.display = 'none';
    errorMessage.style.display = 'block';

    // Show error details if provided
    if (errorDetailsElement && errorDetails) {
      errorDetailsElement.textContent = 'Error details: ' + errorDetails;
      errorDetailsElement.style.display = 'block';
    }

    // Scroll to error message
    setTimeout(() => {
      errorMessage.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }, 100);
  }

  /**
   * Setup conditional fields (enable/disable "Other" text inputs)
   */
  setupConditionalFields() {
    // Business Type "Other" field
    const businessTypeRadios = this.form.querySelectorAll('input[name="contact[business_type]"]');
    const businessTypeOtherInput = this.form.querySelector('input[name="contact[business_type_other]"]');

    if (businessTypeOtherInput) {
      businessTypeOtherInput.disabled = true;

      businessTypeRadios.forEach(radio => {
        radio.addEventListener('change', (e) => {
          if (e.target.value === 'Other') {
            businessTypeOtherInput.disabled = false;
            businessTypeOtherInput.required = true;
            businessTypeOtherInput.focus();
          } else {
            businessTypeOtherInput.disabled = true;
            businessTypeOtherInput.required = false;
            businessTypeOtherInput.value = '';
          }
        });
      });
    }

    // Payment Method "Other" field
    const paymentMethodRadios = this.form.querySelectorAll('input[name="contact[payment_method]"]');
    const paymentMethodOtherInput = this.form.querySelector('input[name="contact[payment_method_other]"]');

    if (paymentMethodOtherInput) {
      paymentMethodOtherInput.disabled = true;

      paymentMethodRadios.forEach(radio => {
        radio.addEventListener('change', (e) => {
          if (e.target.value === 'Other') {
            paymentMethodOtherInput.disabled = false;
            paymentMethodOtherInput.required = true;
            paymentMethodOtherInput.focus();
          } else {
            paymentMethodOtherInput.disabled = true;
            paymentMethodOtherInput.required = false;
            paymentMethodOtherInput.value = '';
          }
        });
      });
    }
  }

  /**
   * Auto-fill contact email if business email is entered
   */
  setupEmailAutofill() {
    const businessEmail = this.form.querySelector('#email');
    const contactEmail = this.form.querySelector('#contact_email');

    if (businessEmail && contactEmail) {
      businessEmail.addEventListener('blur', () => {
        if (businessEmail.value && !contactEmail.value) {
          contactEmail.value = businessEmail.value;
        }
      });
    }
  }

  /**
   * Setup custom validation
   */
  setupValidation() {
    // Validate phone numbers (basic format check)
    const phoneInputs = this.form.querySelectorAll('input[type="tel"]');
    phoneInputs.forEach(input => {
      input.addEventListener('blur', () => {
        const phoneRegex = /^[\d\s\-\+\(\)]+$/;
        if (input.value && !phoneRegex.test(input.value)) {
          input.setCustomValidity('Please enter a valid phone number');
        } else {
          input.setCustomValidity('');
        }
      });
    });

    // Validate URLs
    const urlInputs = this.form.querySelectorAll('input[type="url"]');
    urlInputs.forEach(input => {
      input.addEventListener('blur', () => {
        if (input.value && !input.value.startsWith('http')) {
          input.value = 'https://' + input.value;
        }
      });
    });

    // Validate email domains (optional - check for common typos)
    const emailInputs = this.form.querySelectorAll('input[type="email"]');
    emailInputs.forEach(input => {
      input.addEventListener('blur', () => {
        const email = input.value.toLowerCase();
        const commonTypos = {
          'gmial.com': 'gmail.com',
          'gmai.com': 'gmail.com',
          'yahooo.com': 'yahoo.com',
          'hotmial.com': 'hotmail.com',
        };

        for (const [typo, correct] of Object.entries(commonTypos)) {
          if (email.endsWith(typo)) {
            const suggested = email.replace(typo, correct);
            if (confirm(`Did you mean ${suggested}?`)) {
              input.value = suggested;
            }
            break;
          }
        }
      });
    });

    // Auto-capitalize names
    const nameInputs = this.form.querySelectorAll('#primary_contact_name, #printed_name, #authorized_signature');
    nameInputs.forEach(input => {
      input.addEventListener('blur', () => {
        if (input.value) {
          input.value = input.value
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        }
      });
    });

    // Signature validation - must match printed name (approximately)
    const signatureInput = this.form.querySelector('#authorized_signature');
    const printedNameInput = this.form.querySelector('#printed_name');

    if (signatureInput && printedNameInput) {
      this.form.addEventListener('submit', (e) => {
        const signature = signatureInput.value.trim().toLowerCase();
        const printedName = printedNameInput.value.trim().toLowerCase();

        if (signature && printedName && signature !== printedName) {
          const similar = this.areSimilar(signature, printedName);
          if (!similar) {
            const proceed = confirm(
              'Your signature doesn\'t match your printed name. Continue anyway?'
            );
            if (!proceed) {
              e.preventDefault();
              signatureInput.focus();
            }
          }
        }
      });
    }
  }

  /**
   * Check if two strings are similar (for name validation)
   */
  areSimilar(str1, str2) {
    const normalize = (str) => str.replace(/[^a-z]/g, '');
    const s1 = normalize(str1);
    const s2 = normalize(str2);

    // Check if one contains the other
    if (s1.includes(s2) || s2.includes(s1)) {
      return true;
    }

    // Check Levenshtein distance (allow up to 3 character differences)
    const distance = this.levenshteinDistance(s1, s2);
    return distance <= 3;
  }

  /**
   * Calculate Levenshtein distance between two strings
   */
  levenshteinDistance(str1, str2) {
    const matrix = [];

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }

    return matrix[str2.length][str1.length];
  }
}

// Initialize form when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new DealerApplicationForm();
  });
} else {
  new DealerApplicationForm();
}

